"use client";

/**
 * MaintenanceBanner — najavni banner za predstojeći maintenance.
 *
 * Prikazuje žutu/warning traku iznad topbar-a kad backend vrati
 * announcement (active=true) iz GET /api/v3/maintenance/announcement.
 *
 * Klijentska komponenta:
 * - Mount-time fetch + interval poll (default 5min)
 * - Dismiss preko sessionStorage (per-tab perzistencija)
 * - Dark mode kompatibilan (CSS varijable)
 * - PRAVILO 12: Manrope za naslov, Inter za body, CSS vars only
 *
 * Integracija:
 *   apps/web-erp/src/app/layout.tsx:
 *     <MaintenanceBanner apiBase="https://api.grozd.me" />
 *   apps/web-admin/src/app/layout.tsx: isto
 *
 * NE dodavati u auth ili marketing — banner je za autenticirane korisnike
 * (interni najavni signal). Marketing landing ima sopstveni status sistem.
 */

import * as React from "react";

const SESSION_STORAGE_KEY_PREFIX = "grozd_maintenance_banner_dismissed:";
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min
/**
 * Banner sluša ovaj event da forsira instant refetch announcement-a.
 * Admin /sistem/deploy ProdukcijaPanel ga dispatch-uje nakon cancel/trigger,
 * tako da banner gore i "AKTUELAN schedule" sekcija dolje uvijek pokazuju
 * isto stanje (Velibor 2026-05-03).
 */
const SCHEDULE_CHANGED_EVENT = "grozd:maintenance-schedule-changed";

interface AnnouncementResponse {
  active: boolean;
  scheduled_at?: string | null;
  eta_minutes?: number | null;
  announcement_active_from?: string | null;
  message?: string | null;
}

export interface MaintenanceBannerProps {
  /** API base URL (npr. https://api.grozd.me ili https://api.dev.grozd.me) */
  apiBase?: string;
  /** Override poll interval u ms (default 5min) */
  pollIntervalMs?: number;
}

function fmtDateTime(iso?: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("sr-Latn-ME", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function defaultApiBase(): string {
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    // dev.grozd.me / *.dev.grozd.me → api.dev.grozd.me
    if (host.endsWith(".dev.grozd.me") || host === "dev.grozd.me") {
      return "https://api.dev.grozd.me";
    }
  }
  // env override (build-time)
  if (typeof process !== "undefined" && process.env?.NEXT_PUBLIC_API_URL) {
    return process.env.NEXT_PUBLIC_API_URL;
  }
  return "https://api.grozd.me";
}

export function MaintenanceBanner({
  apiBase,
  pollIntervalMs = POLL_INTERVAL_MS,
}: MaintenanceBannerProps) {
  const base = apiBase ?? defaultApiBase();
  const [data, setData] = React.useState<AnnouncementResponse | null>(null);
  const [dismissed, setDismissed] = React.useState(false);

  // Storage key se mijenja po scheduled_at — ako admin promijeni schedule,
  // dismiss se automatski poništi i korisnik vidi novu najavu
  const storageKey = React.useMemo(() => {
    if (!data?.scheduled_at) return null;
    return `${SESSION_STORAGE_KEY_PREFIX}${data.scheduled_at}`;
  }, [data?.scheduled_at]);

  // Ucitaj dismiss state kad se promijeni storageKey
  React.useEffect(() => {
    if (!storageKey || typeof window === "undefined") {
      setDismissed(false);
      return;
    }
    try {
      const v = window.sessionStorage.getItem(storageKey);
      setDismissed(v === "1");
    } catch {
      setDismissed(false);
    }
  }, [storageKey]);

  // Fetch announcement (mount + interval + custom event)
  React.useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setInterval> | null = null;

    async function load() {
      try {
        const r = await fetch(`${base}/api/v3/maintenance/announcement`, {
          credentials: "include",
          // Ne kešuj — admin može promijeniti schedule u realnom vremenu
          cache: "no-store",
        });
        if (!r.ok) {
          // Graceful skip (PRAVILO 20 — bez 404/500 errora u konzoli)
          if (!cancelled) setData(null);
          return;
        }
        const body = (await r.json()) as AnnouncementResponse;
        if (!cancelled) setData(body);
      } catch {
        // Network error — banner se ne prikazuje, ali ne pravi greške u konzoli
        if (!cancelled) setData(null);
      }
    }

    void load();
    timer = setInterval(load, pollIntervalMs);

    // Forced refetch kad admin promijeni schedule (cancel/trigger u istoj
    // sesiji) — bez ovog, banner bi mogao 5 minuta da pokazuje stari stanje
    // dok se ne završi sledeći poll ciklus.
    function onScheduleChanged() {
      void load();
    }
    if (typeof window !== "undefined") {
      window.addEventListener(SCHEDULE_CHANGED_EVENT, onScheduleChanged);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      if (typeof window !== "undefined") {
        window.removeEventListener(SCHEDULE_CHANGED_EVENT, onScheduleChanged);
      }
    };
  }, [base, pollIntervalMs]);

  function handleDismiss() {
    if (storageKey && typeof window !== "undefined") {
      try {
        window.sessionStorage.setItem(storageKey, "1");
      } catch {
        // sessionStorage može pasti u SSR/incognito — ignoriši
      }
    }
    setDismissed(true);
  }

  if (!data || !data.active || dismissed) {
    return null;
  }

  const when = fmtDateTime(data.scheduled_at);
  const eta = data.eta_minutes ?? 30;
  const customMsg = (data.message ?? "").trim();

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        background: "var(--color-grozd-warning-bg, #FEF3C7)",
        color: "var(--color-grozd-warning-ink, #78350F)",
        borderBottom:
          "1px solid var(--color-grozd-warning-border, #FCD34D)",
        padding: "10px 16px",
        fontFamily: "var(--font-body), Inter, system-ui, sans-serif",
        fontSize: 13,
        fontWeight: 500,
        position: "relative",
        zIndex: 50,
      }}
    >
      <div
        style={{
          maxWidth: 1280,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
          <span
            aria-hidden="true"
            style={{ fontSize: 16, lineHeight: 1 }}
            title="Najava održavanja"
          >
            {/* Triangle warning ikona — inline SVG da ne traži dodatne deps */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ display: "inline-block", verticalAlign: "middle" }}
            >
              <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
              <line x1="12" y1="9" x2="12" y2="13" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </span>
          <span>
            <strong
              style={{
                fontFamily:
                  "var(--font-brand), Manrope, system-ui, sans-serif",
                fontWeight: 700,
                marginRight: 6,
              }}
            >
              Najavljeno održavanje:
            </strong>
            sistem će biti privremeno nedostupan{" "}
            <strong>{when}</strong> u trajanju ~{eta} min.
            {customMsg ? <> {customMsg}</> : null}
          </span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Sakrij najavu"
          title="Sakrij najavu (do nove sesije)"
          style={{
            background: "transparent",
            border: "none",
            color: "inherit",
            cursor: "pointer",
            padding: "4px 8px",
            fontSize: 18,
            lineHeight: 1,
            opacity: 0.7,
            flexShrink: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.7")}
        >
          ×
        </button>
      </div>
    </div>
  );
}
